import React from 'react';
import PropTypes from 'prop-types';
import * as d3 from 'd3';
import { Curve } from '../models/Curve';

class IdealCurvePlot extends React.Component {
  static propTypes = {
    curve: PropTypes.instanceOf(Curve),
    width: PropTypes.number,
    height: PropTypes.number,
    padding: PropTypes.number,
    points: PropTypes.arrayOf(PropTypes.object).isRequired,
    curve: PropTypes.instanceOf(Curve),
  };

  static defaultProps = {
    width: 500,
    height: 500,
    padding: 40,
  };

  constructor(props) {
    super(props);

    this.chartContainerRef = React.createRef();
    this.chartRef = React.createRef();
    this.svgWidth = props.width;
    this.svgHeight = props.height;
    const padding = props.padding;
    this.svgPadding = { top: padding, right: padding, bottom: padding, left: padding };
    this.height = this.svgHeight - this.svgPadding.top - this.svgPadding.bottom;
    this.width = this.svgWidth - this.svgPadding.left - this.svgPadding.right;
    this.transitionDuration = 1000;
    this.curvePointCount = 500;
    this.hoverPercentage = 0.02;
    this.windowPercentage = 0.1;
    this.pointColor = "#000000";
    this.lineColor = "#4682b4";
    this.pointRadius = 3;
    this.lineStrokeWidth = 1.5;

    this.state = {
      minX: 0,
      maxX: 10,
      minY: 0,
      maxY: 10,
    };
  }

  componentDidMount() {
    this._drawChart();
  }

  componentDidUpdate() {
    this._drawChart();
  }

  _updatePlotDomains = () => {
    const { points } = this.props;

    if (points.length == 0) return;

    const xValues = points.map(p => p.x);
    const yValues = points.map(p => p.y);

    const minX = Math.min(...xValues);
    const maxX = Math.max(...xValues);
    const xPadding = (maxX - minX) * this.windowPercentage;
    const minXDomain = minX - xPadding;
    const maxXDomain = maxX + xPadding;

    const minY = Math.min(...yValues);
    const maxY = Math.max(...yValues);
    const yPadding = (maxY - minY) * this.windowPercentage;
    const minYDomain = minY - yPadding;
    const maxYDomain = maxY + yPadding;

    let stateUpdate = {};

    if (minXDomain != this.state.minX) stateUpdate.minX = minXDomain;
    if (maxXDomain != this.state.maxX) stateUpdate.maxX = maxXDomain;
    if (minYDomain != this.state.minY) stateUpdate.minY = minYDomain;
    if (maxYDomain != this.state.maxY) stateUpdate.maxY = maxYDomain;

    if (Object.keys(stateUpdate).length != 0) this.setState(stateUpdate);
  };

  _drawChart = () => {
    let svg = d3.select(this.chartRef.current)
      .attr(
        "viewBox",
        "0 0 " + this.svgWidth + " " + this.svgHeight
      )
      .selectAll('g.plot')
      .data([{ left: this.svgPadding.left, top: this.svgPadding.top }])
      .join('g')
      .attr('class', 'plot')
      .attr("transform", d => `translate(${d.left},${d.top})`);

    this._updatePlotDomains();
    const xAxis = this._drawXAxis(svg);
    const yAxis = this._drawYAxis(svg);

    const { curve } = this.props;
    if (curve == null) return;

    let data = []
    for (let i = 0; i < this.curvePointCount; i++) {
      const x = this.state.minX + i * (this.state.maxX - this.state.minX) / this.curvePointCount;
      data.push({ x: x, y: curve.y(x) });
    }

    this._drawLine(xAxis, yAxis, svg, data);
    this._drawPoints(xAxis, yAxis, svg);
    this._drawMouseover(xAxis, yAxis, svg, data);
  };

  _drawXAxis = (svg) => {
    const { xLabel } = this.props;

    const x = d3.scaleLinear()
      .domain([this.state.minX, this.state.maxX])
      .range([0, this.width]);

    svg.selectAll('g.x-axis')
      .data([xLabel])
      .join('g')
      .attr('class', 'x-axis')
      .attr("transform", `translate(0,${this.height})`)
      .call(d3.axisBottom(x))
      .selectAll('text.x-axis-label')
      .data([xLabel])
      .join('text')
      .attr('class', 'x-axis-label')
      .attr('fill', 'currentColor')
      .text(d => d)
      .attr('x', this.svgPadding.left + (this.width - this.svgPadding.left - this.svgPadding.right) / 2)
      .attr('y', 30); // Relative to the x axis.

    return x;
  };

  _drawYAxis = (svg) => {
    const { yLabel } = this.props;

    const y = d3.scaleLinear()
      .domain([this.state.minY, this.state.maxY])
      .range([this.height, 0]);

    svg.selectAll('g.y-axis')
      .data([yLabel])
      .join('g')
      .attr('class', 'y-axis')
      .call(d3.axisLeft(y))
      .selectAll('text.y-axis-label')
      .data([yLabel])
      .join('text')
      .attr('class', 'y-axis-label')
      .attr('fill', 'currentColor')
      .text(d => d)
      .attr('transform', 'rotate(-90)')
      .attr('x', -(this.svgPadding.top + (this.height - this.svgPadding.top - this.svgPadding.bottom) / 2))
      .attr('y', -30); // Relative to the y axis.

    return y;
  };

  _drawLine = (xAxis, yAxis, svg, data) => {
    svg.selectAll('path.path')
      .data([data])
      .join(
        enter => enter.append("path")
          .attr("d", d3.line()
            .x(d => xAxis(d.x))
            .y(d => yAxis(0))
          )
          .transition()
          .duration(this.transitionDuration)
          .attr("d", d3.line()
            .x(d => xAxis(d.x))
            .y(d => yAxis(d.y))
          ),
        merge => merge.transition()
          .duration(this.transitionDuration)
          .attr("d", d3.line()
            .x(d => xAxis(d.x))
            .y(d => yAxis(d.y))
          ),
        exit => exit.transition()
          .duration(this.transitionDuration)
          .attr("d", d3.line()
            .x(d => xAxis(d.x))
            .y(d => yAxis(0))
          )
          .style('opacity', 0)
          .remove(),
      )
      .attr('class', 'path')
      .attr("fill", "none")
      .attr("stroke", this.lineColor)
      .attr("stroke-width", this.lineStrokeWidth);
  };

  _drawPoints = (xAxis, yAxis, svg) => {
    const { points } = this.props;

    svg.selectAll('circle.point')
      .data(points)
      .join(
        enter => enter.append("circle")
          .attr("cx", d => xAxis(d.x))
          .attr("cy", d => yAxis(0))
          .transition()
          .duration(this.transitionDuration)
          .attr("cy", d => yAxis(d.y)),
        merge => merge.transition()
          .duration(this.transitionDuration)
          .attr("cx", d => xAxis(d.x))
          .attr("cy", d => yAxis(d.y)),
        exit => exit.transition()
          .duration(this.transitionDuration)
          .attr("cy", d => yAxis(0))
          .style('opacity', 0)
          .remove(),
      )
      .attr('class', 'point')
      .attr("r", this.pointRadius)
      .attr("fill", this.pointColor);
  };

  _drawMouseover = (xAxis, yAxis, svg, data) => {
    const { xLabel, yLabel } = this.props;

    const bisect = d3.bisector(d => d.x).left;

    svg.selectAll('g.focus').remove();
    const focus = svg.append('g')
      .attr('class', 'focus')
      .append('circle')
      .style("fill", this.lineColor)
      .attr('r', this.pointRadius)
      .style("opacity", 0);

    const container = d3.select(this.chartContainerRef.current);
    container.select('div#ideal-curve-editor-tooltip').remove();
    const tooltip = container
      .append('div')
      .attr('id', 'ideal-curve-editor-tooltip')
      .style("position", "absolute")
      .style("opacity", "0")
      .style("background-color", "white")
      .style("border", "solid")
      .style("border-width", "1px")
      .style("border-radius", "5px")
      .style("padding", "10px");

    svg.selectAll('rect.mouseover-rect').remove();
    svg.append('rect')
      .attr('class', 'mouseover-rect')
      .style("fill", "none")
      .style("pointer-events", "all")
      .attr('width', this.width)
      .attr('height', this.height)
      .on('mousemove', (event) => {
        const pointerPosition = d3.pointer(event);
        const pointerXPositionOnGraph = xAxis.invert(pointerPosition[0]);
        const i = bisect(data, pointerXPositionOnGraph, 1);
        const selectedData = data[i];
        if (selectedData && Math.abs(yAxis(selectedData.y) - pointerPosition[1]) <= this.height * this.hoverPercentage) {
          focus
            .attr("cx", xAxis(selectedData.x))
            .attr("cy", yAxis(selectedData.y));
          tooltip
            .html(`${xLabel}: ${selectedData.x.toFixed(2)}<br />${yLabel}: ${selectedData.y.toFixed(2)}`)
            .style("top", (yAxis(selectedData.y) + this.svgPadding.top + this.svgPadding.bottom) + "px").style("left", (xAxis(selectedData.x) + + this.svgPadding.left + this.svgPadding.right) + "px")
          focus.transition()
            .duration(this.transitionDuration)
            .style("opacity", 1);
          tooltip
            .style("visibility", "visible")
            .transition()
            .duration(this.transitionDuration)
            .style("opacity", 1);
        } else {
          focus.transition()
            .duration(this.transitionDuration)
            .style("opacity", 0);
          tooltip.transition()
            .duration(this.transitionDuration)
            .style("opacity", 0)
            .on("end", () => tooltip.style("visibility", "hidden"));
        }
      })
      .on('mouseout', () => {
        focus.transition()
          .duration(this.transitionDuration)
          .style("opacity", 0);
        tooltip.transition()
          .duration(this.transitionDuration)
          .style("opacity", 0)
          .on("end", () => tooltip.style("visibility", "hidden"));
      });
  };

  render() {
    return <div ref={this.chartContainerRef}><svg ref={this.chartRef} role="img" /></div>;
  }
}

export { IdealCurvePlot };
